import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, Send, Linkedin, Instagram, Twitter, Youtube, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../i18n";
import { Logo } from "./Logo";

const reveal = {
  initial: { opacity: 0, y: 28 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-[#062316] outline-none transition-colors duration-300 placeholder:text-slate-400 focus:border-[#48e38e] focus:bg-white";

export const ContactFooter = () => {
  const { t, lang } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", country: "", service: "", message: "" });
  const [newsletter, setNewsletter] = useState("");

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const post = async (path, body) => {
    const res = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...body, lang }),
    });
    if (!res.ok) throw new Error("request failed");
  };

  const submitContact = async (e) => {
    e.preventDefault();
    try {
      await post("/contact", form);
      toast.success(t.toasts.contact);
      setForm({ name: "", email: "", country: "", service: "", message: "" });
    } catch {
      toast.error(t.toasts.error);
    }
  };

  const submitNewsletter = async (e) => {
    e.preventDefault();
    try {
      await post("/newsletter", { email: newsletter });
      toast.success(t.toasts.newsletter);
      setNewsletter("");
    } catch {
      toast.error(t.toasts.error);
    }
  };

  const infoItems = [
    { icon: MapPin, title: t.contact.info.hqTitle, value: t.contact.info.hq, testid: "contact-info-hq" },
    { icon: MapPin, title: t.contact.info.officeTitle, value: t.contact.info.office, testid: "contact-info-office" },
    { icon: Phone, title: t.contact.info.phoneTitle, value: t.contact.info.phone, testid: "contact-info-phone" },
    { icon: Mail, title: t.contact.info.emailTitle, value: t.contact.info.email, testid: "contact-info-email" },
  ];

  const socials = [
    { icon: Linkedin, label: "LinkedIn", testid: "social-linkedin" },
    { icon: Instagram, label: "Instagram", testid: "social-instagram" },
    { icon: Twitter, label: "X", testid: "social-twitter" },
    { icon: Youtube, label: "YouTube", testid: "social-youtube" },
  ];

  return (
    <>
      <section id="iletisim" data-testid="contact-section" className="bg-[#f4faf7] py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <motion.p {...reveal} className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-[#1d8a52]">
              {t.contact.overline}
            </motion.p>
            <motion.h2
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.08 }}
              className="font-display mt-4 text-2xl font-bold leading-tight tracking-tight text-[#062316] sm:text-3xl lg:text-4xl"
              data-testid="contact-title"
            >
              {t.contact.title}
            </motion.h2>
            <motion.p
              {...reveal}
              transition={{ ...reveal.transition, delay: 0.15 }}
              className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-slate-600"
            >
              {t.contact.sub}
            </motion.p>
          </div>
        </div>
        <div className="mx-auto mt-16 grid max-w-7xl gap-16 px-5 sm:px-8 lg:grid-cols-2">
          <div>
            <div className="space-y-6">
              {infoItems.map((item, i) => (
                <motion.div
                  key={item.testid}
                  {...reveal}
                  transition={{ ...reveal.transition, delay: 0.1 + 0.06 * i }}
                  className="flex items-start gap-4"
                  data-testid={item.testid}
                >
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#062316] text-[#48e38e]">
                    <item.icon size={18} />
                  </span>
                  <div>
                    <h3 className="font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-[#1d8a52]">
                      {item.title}
                    </h3>
                    <p className="mt-1 max-w-sm text-sm leading-relaxed text-slate-700">{item.value}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <motion.form
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.12 }}
            onSubmit={submitContact}
            className="rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_24px_60px_-32px_rgba(6,35,22,0.25)] sm:p-10"
            data-testid="contact-form"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <input
                data-testid="contact-form-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t.contact.form.name}
                className={inputCls}
              />
              <input
                data-testid="contact-form-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder={t.contact.form.email}
                className={inputCls}
              />
              <input
                data-testid="contact-form-country"
                required
                value={form.country}
                onChange={(e) => setForm({ ...form, country: e.target.value })}
                placeholder={t.contact.form.country}
                className={inputCls}
              />
              <select
                data-testid="contact-form-service"
                required
                value={form.service}
                onChange={(e) => setForm({ ...form, service: e.target.value })}
                className={`${inputCls} ${form.service ? "" : "text-slate-400"}`}
              >
                <option value="" disabled>
                  {t.contact.form.service}
                </option>
                {t.quote.products.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              data-testid="contact-form-message"
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              placeholder={t.contact.form.message}
              className={`${inputCls} mt-4 resize-none`}
            />
            <button
              data-testid="contact-form-submit"
              type="submit"
              className="group mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-[#48e38e] px-6 py-3.5 text-sm font-semibold text-[#062316] transition-all duration-300 hover:shadow-[0_0_28px_rgba(72,227,142,0.5)]"
            >
              {t.contact.form.submit}
              <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.form>
        </div>
      </section>

      <section data-testid="newsletter-band" className="bg-[#48e38e] py-14">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 sm:px-8 lg:flex-row lg:items-center">
          <motion.div {...reveal}>
            <h3 className="font-display text-2xl font-bold tracking-tight text-[#062316] sm:text-3xl" data-testid="newsletter-title">
              {t.contact.newsletter.title}
            </h3>
            <p className="mt-2 max-w-md text-sm font-medium text-[#0a3a22]">{t.contact.newsletter.sub}</p>
          </motion.div>
          <motion.form {...reveal} onSubmit={submitNewsletter} className="flex w-full max-w-md gap-3">
            <input
              data-testid="newsletter-input"
              required
              type="email"
              value={newsletter}
              onChange={(e) => setNewsletter(e.target.value)}
              placeholder={t.contact.newsletter.placeholder}
              className="w-full rounded-full border border-[#062316]/20 bg-white/90 px-5 py-3.5 text-sm text-[#062316] outline-none placeholder:text-slate-500 focus:border-[#062316]"
            />
            <button
              data-testid="newsletter-submit"
              type="submit"
              className="group flex shrink-0 items-center gap-2 rounded-full bg-[#062316] px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#04120b]"
            >
              {t.contact.newsletter.button}
              <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
          </motion.form>
        </div>
      </section>

      <footer data-testid="footer" className="bg-[#04120b] pt-16 pb-8">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Logo />
              <p className="mt-5 max-w-sm text-sm leading-relaxed text-white/50">{t.contact.footerTagline}</p>
              <div className="mt-7 flex items-center gap-3">
                {socials.map((s) => (
                  <a
                    key={s.testid}
                    href="#"
                    aria-label={s.label}
                    data-testid={s.testid}
                    onClick={(e) => e.preventDefault()}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/60 transition-all duration-300 hover:border-[#48e38e] hover:text-[#48e38e]"
                  >
                    <s.icon size={16} />
                  </a>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-7">
              {t.contact.footerCols.map((col) => (
                <div key={col.title}>
                  <h4 className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#48e38e]">
                    {col.title}
                  </h4>
                  <ul className="mt-5 space-y-3">
                    {col.links.map((link) => (
                      <li key={link}>
                        <a
                          href="#"
                          onClick={(e) => e.preventDefault()}
                          className="text-sm text-white/55 transition-colors duration-300 hover:text-white"
                        >
                          {link}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-14 flex flex-col items-start justify-between gap-4 border-t border-white/10 pt-7 sm:flex-row sm:items-center">
            <p className="text-xs text-white/40" data-testid="footer-rights">
              {t.contact.rights}
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#48e38e]/70" data-testid="footer-certs">
              {t.contact.certs}
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};
